import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, User, Wrench, Package, Calendar, Award, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

const ResourceSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    skill: '',
    min_level: '',
    available_from: '',
    available_to: '',
    search: ''
  });

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const response = await axiosInstance.get(`/resources/search?${params.toString()}`);
      setResources(response.data);
      
      if (response.data.length === 0) {
        toast.info('Arama kriterlerine uygun kaynak bulunamadı');
      }
    } catch (error) {
      toast.error('Arama başarısız');
    } finally {
      setLoading(false);
    }
  };

  const getResourceIcon = (type) => {
    const icons = {
      human: <User className="text-blue-600" size={24} />,
      equipment: <Wrench className="text-orange-600" size={24} />,
      material: <Package className="text-green-600" size={24} />
    };
    return icons[type] || <User size={24} />;
  };

  const getResourceTypeLabel = (type) => {
    const labels = {
      human: 'İnsan Kaynağı',
      equipment: 'Ekipman',
      material: 'Malzeme'
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('backToDashboard')}
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('resourceSearch')}</h1>
            <p className="text-gray-600 mt-1">{t('findResourcesBySkills')}</p>
          </div>
        </div>

        {/* Search Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('resourceType')}
              </label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({...filters, type: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">{t('all')}</option>
                <option value="human">{t('human')}</option>
                <option value="equipment">{t('equipment')}</option>
                <option value="material">{t('material')}</option>
              </select>
            </div>

            {/* Skill Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('skill')}
              </label>
              <input
                type="text"
                value={filters.skill}
                onChange={(e) => setFilters({...filters, skill: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="React, Python, etc."
              />
            </div>

            {/* Min Level Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('minLevel')}
              </label>
              <select
                value={filters.min_level}
                onChange={(e) => setFilters({...filters, min_level: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Tümü</option>
                <option value="1">1 - Başlangıç</option>
                <option value="2">2 - Temel</option>
                <option value="3">3 - Orta</option>
                <option value="4">4 - İleri</option>
                <option value="5">5 - Uzman</option>
              </select>
            </div>

            {/* Available From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Müsaitlik Başlangıcı
              </label>
              <input
                type="date"
                value={filters.available_from}
                onChange={(e) => setFilters({...filters, available_from: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            {/* Available To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Müsaitlik Bitişi
              </label>
              <input
                type="date"
                value={filters.available_to}
                onChange={(e) => setFilters({...filters, available_to: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            {/* Name Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                İsim
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="Kaynak ismi..."
              />
            </div>
          </div>

          <button
            onClick={handleSearch}
            disabled={loading}
            className="w-full md:w-auto bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Search size={20} />
            {loading ? 'Aranıyor...' : 'Ara'}
          </button>
        </div>

        {/* Results */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : resources.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Search size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Sonuç Yok</h3>
            <p className="text-gray-600">Arama yapmak için yukarıdaki filtreleri kullanın.</p>
          </div>
        ) : (
          <div>
            <div className="mb-4 text-sm text-gray-600">
              {resources.length} kaynak bulundu
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.map(resource => (
                <div key={resource.id} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      {getResourceIcon(resource.type)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{resource.name}</h3>
                      <p className="text-sm text-gray-600">{getResourceTypeLabel(resource.type)}</p>
                    </div>
                  </div>

                  {/* Human Resource Details */}
                  {resource.type === 'human' && resource.type_specific_data && (
                    <div className="space-y-3">
                      {resource.type_specific_data.role && (
                        <div className="flex items-center text-sm text-gray-600">
                          <User size={16} className="mr-2" />
                          {resource.type_specific_data.role}
                        </div>
                      )}
                      
                      {resource.type_specific_data.skills && resource.type_specific_data.skills.length > 0 && (
                        <div>
                          <div className="flex items-center text-sm text-gray-700 font-medium mb-2">
                            <Award size={16} className="mr-2" />
                            Yetenekler
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {resource.type_specific_data.skills.map((skillData, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium"
                              >
                                {skillData.skill} (Lvl {skillData.level})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {resource.type_specific_data.capacity_per_week && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar size={16} className="mr-2" />
                          {resource.type_specific_data.capacity_per_week} saat/hafta
                        </div>
                      )}
                      
                      {resource.type_specific_data.hourly_rate && (
                        <div className="text-sm font-medium text-green-600">
                          ${resource.type_specific_data.hourly_rate}/saat
                        </div>
                      )}
                    </div>
                  )}

                  {/* Equipment Details */}
                  {resource.type === 'equipment' && resource.type_specific_data && (
                    <div className="space-y-2 text-sm text-gray-600">
                      {resource.type_specific_data.location && (
                        <div>Lokasyon: {resource.type_specific_data.location}</div>
                      )}
                      {resource.type_specific_data.cost_per_use && (
                        <div className="text-green-600 font-medium">
                          ${resource.type_specific_data.cost_per_use}/kullanım
                        </div>
                      )}
                    </div>
                  )}

                  {/* Material Details */}
                  {resource.type === 'material' && resource.type_specific_data && (
                    <div className="space-y-2 text-sm text-gray-600">
                      {resource.type_specific_data.stock_quantity !== undefined && (
                        <div>Stok: {resource.type_specific_data.stock_quantity} {resource.type_specific_data.unit || 'adet'}</div>
                      )}
                      {resource.type_specific_data.unit_price && (
                        <div className="text-green-600 font-medium">
                          ${resource.type_specific_data.unit_price}/{resource.type_specific_data.unit || 'adet'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceSearch;
